import { useCallback } from "react";

import useTypedContext from "hooks/useTypedContext";
import useTypedFlags from "hooks/useTypedFlags";
import { AccountContext } from "views/AccountWrapper";
import { Context } from "types/generated";
import { hasSpaceManageAccess } from "utils/user";
import { hasManageAccess, AccessResult, NO_ACCESS_MESSAGE } from "utils/access";

/**
 * Hook that returns a function to check if the current user can lock a stack.
 * A user needs write access to lock a stack.
 * A user can't lock a stack that is already locked.
 * @returns {(stack: Stack) => AccessResult} Function that takes a stack and returns an AccessResult indicating if user can lock it
 */
const useCanManageContext = (): ((context: Context) => AccessResult) => {
  const { viewer } = useTypedContext(AccountContext);
  const { customAccessRoles } = useTypedFlags();

  return useCallback(
    (context: Context) => {
      if (viewer.admin) {
        return { result: true };
      }
      const result = customAccessRoles
        ? hasManageAccess(viewer, context.space)
        : hasSpaceManageAccess(context.spaceDetails.accessLevel);

      if (!result) {
        return { result, message: NO_ACCESS_MESSAGE };
      }

      return { result: true };
    },
    [customAccessRoles, viewer]
  );
};

export default useCanManageContext;
